import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../../api/client';
import { ICM_LIST_TAKE, icmTruncationNote } from '../icmPaging';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { useNotify } from '../../../components/notifications';
import { useNavActions } from '../../../hooks/useNavActions';
import { BusyIcon } from '../../../components/loading';
import { DateFilterSelect, monthsOf, type DateFilterId, DEFAULT_DATE_FILTER } from './IcmDateFilter';
import { useConfirm } from '../../../components/confirm';
import { fmtLocalDate } from '../../../utils/datetime';
import { useAuthStore } from '../../../auth/store';
import '../../backlog/backlog.css';
import '../../modules/SettingsDetailPage.css';

interface ConsumptionRow {
  oid: string;
  lt: string | null;
  vendorLt: string | null;       // not a grid column; G5's search reads it
  hiddenSearchText: string | null;  // the other values G5's search reads and no column shows
  itemName: string | null;
  itemCode: string | null;
  storageName: string | null;
  givenToName: string | null;
  totalItems: number;
  distributionDate: string | null;
  statusLabel: string;
  // legacy Consumption / Disposal grid columns
  vendorName: string | null;
  specification: string | null;
  amount: string | null;
  amountUnits: string | null;
  alertQty: number | null;
  catalog: string | null;        // Items.VendorCatName — the History Catalog# column
  expiryDate: string | null;
  receiveDate: string | null;
  consumptionDate: string | null;
  consumptionByName: string | null;
  disposedDate: string | null;
  disposedByName: string | null;
}

/** Item Disposal's legacy "Mode" action (DisposalViewController) — the grid is
 *  filtered to expired / non-expired / all lots. */
/** /distributions/employee-options rows. The endpoint returns all four name
 *  parts; fullName is what the legacy "Consumed By" lookup displays. */
type EmployeeOpt = {
  oid: string; displayName?: string | null; firstName?: string | null;
  lastName?: string | null; fullName?: string | null;
};
const empName = (o: EmployeeOpt) =>
  o.fullName || o.displayName ||
  [o.firstName, o.lastName].filter(Boolean).join(' ') || o.oid;

type DisposalMode = 'expired' | 'nonexpired' | 'all';
const DISPOSAL_MODES: Array<{ id: DisposalMode; label: string }> = [
  { id: 'expired',    label: 'Expired Items' },
  { id: 'nonexpired', label: 'Non-Expired Items' },
  { id: 'all',        label: 'All Items' },
];

interface ConsumptionListPageProps {
  formType?: 'consume' | 'dispose';
  pageId?: string;
  /** Nav id whose MAC rights gate this page's actions (Consumption vs Disposal). */
  navId?: string;
  title?: string;
}

export default function ConsumptionListPage({
  formType = 'consume',
  pageId = 'icm-consumptions',
  navId = 'icm-consumption',
  title = 'Item Consumption',
}: ConsumptionListPageProps) {
  const notify = useNotify();
  const { confirm, prompt } = useConfirm();
  // MAC rights (hide, never grey): consume / dispose / returns / the ConsumedBy cell = Write.
  const perm = useNavActions(navId);

  const [rows, setRows] = useState<ConsumptionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [acting, setActing] = useState(false);
  const [returning, setReturning] = useState(false);
  const [disposalMode, setDisposalMode] = useState<DisposalMode>('all');
  /** Consumed By — legacy's INLINE GRID column (Distribution_ListView_Consumption
   *  ColumnInfo "ConsumptionBy", no AllowEdit="False"; Consume_Execute calls
   *  Grid.UpdateEdit() before reading it). It is picked PER ROW in the grid, not
   *  once on the toolbar — a batch can hand different lots to different people.
   *  oid -> chosen Employee oid for the rows edited but not yet saved. */
  const [consumedByRow, setConsumedByRow] = useState<Record<string, string>>({});
  const [employees, setEmployees] = useState<EmployeeOpt[]>([]);

  useEffect(() => {
    if (formType !== 'consume') return;
    api.get('/api/v1/distributions/employee-options')
      .then(r => setEmployees((r.data ?? []) as EmployeeOpt[]))
      .catch(() => setEmployees([]));
  }, [formType]);

  const actionVerb = formType === 'dispose' ? 'dispose' : 'consume';
  const actionLabel = formType === 'dispose' ? 'Dispose' : 'Save';
  /** Barcode: legacy scans an LT and filters the grid to THAT row
   *  (ConsumptionViewController.TextBoxAction_ValueChanged -> [LT] = ?), showing
   *  an error when the LT is unknown. Sent as an EXACT @Lt filter, not folded
   *  into Search, which is a LIKE. */
  const [barcode, setBarcode] = useState('');
  const [ltFilter, setLtFilter] = useState('');
  /** History: the consume / return trail of one row (dbo.ConsumptionHistory). */
  /** Legacy's History action (Consumeview) opens
   *  Distribution_ListView_ConsumptionViewmode, whose Criteria is EMPTY -- EVERY
   *  record rather than only the pending ones. This is the VIEW-MODE toggle; the
   *  per-row consume/return trail is the separate "Item History" button below. */
  const [viewMode, setViewMode] = useState(false);
  const [dateFilter, setDateFilter] = useState<DateFilterId>(DEFAULT_DATE_FILTER);
  const baseUrl = formType === 'dispose' ? '/api/v1/disposals' : '/api/v1/consumptions';

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      const res = await api.get(baseUrl, {
        params: {
          /* The API's own default is 50 -- fine for a short pending list, far
             too small for History, which is "every consumption record".

             This WAS 500, and 500 was not a page size: the grid pages on the
             client, so it was a hard CEILING. Measured on the live division
             2026-09-30: History holds 8,389 rows and the queue 1,091, so the
             grid footer read "500 items / 25 pages" on both -- the cap, not the
             count -- and rows 501+ could not be reached, searched, sorted or
             column-filtered at all.
             The cap bought nothing: the same call returns all 8,389 rows in
             250 ms against 190 ms for 500 (the SP already computes the set;
             OFFSET/FETCH only trims what it sends). Well inside the section 12.5
             budget of 1,500 ms for a 10k-row grid. */
          take: ICM_LIST_TAKE,
          formType,
          ...(formType === 'dispose' ? { disposalMode } : {}),
          ...(ltFilter ? { lt: ltFilter } : {}),
          history: viewMode,
          /* ONLY WHERE THE PICKER IS. The Date Filter renders in History alone
             (legacy targets the view-mode lists), but `months` was sent on BOTH
             loads from the same state. Harmless while that state defaulted to
             ALL; once it defaulted to 3M it emptied the PENDING queues, because
             the window applies to ConsumptionDate / DisposedDate and a row that
             is still pending has neither -- consume 1,094 rows -> 0, dispose
             5,000 -> 0, with no visible control to undo it. A filter is only
             ever sent for a view that shows its control. */
          ...(viewMode ? { months: monthsOf(dateFilter) } : {}),
        },
      });
      const body = res.data as { items?: ConsumptionRow[]; total?: number };
      const items = body.items ?? [];
      setRows(items);
      /* The SP returns the true row count (COUNT(*) OVER (), computed before
         OFFSET/FETCH), so a short read is detectable rather than silent. If the
         cap above is ever reached again, the grid says so instead of quietly
         showing a prefix -- which is exactly how the 500 went unnoticed. */
      const note = icmTruncationNote(items.length, body.total);
      if (note) notify.info(note);
      // Legacy tells you when a scanned LT is not on this list rather than just
      // showing an empty grid.
      if (ltFilter && items.length === 0) notify.error(`LT ${ltFilter} was not found.`);
    } catch {
      setError(`Failed to load ${formType} items.`);
      setRows([]);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [formType, disposalMode, baseUrl, ltFilter, viewMode, dateFilter, notify]);

  useEffect(() => { void load(); }, [load]);

  /** The TOP-BAR title follows the view, as legacy's does — History opens
   *  Distribution_ListView_ConsumptionViewmode. Uses the shared
   *  `ai:topbar-title` override. Disposal keeps its own name. */
  useEffect(() => {
    const t = !viewMode ? null
      : formType === 'dispose' ? 'Item Disposal History' : 'Item Consumption History';
    window.dispatchEvent(new CustomEvent('ai:topbar-title', { detail: t }));
    return () => { window.dispatchEvent(new CustomEvent('ai:topbar-title', { detail: null })); };
  }, [viewMode, formType]);

  const txt = (v: string | null | undefined) => v ?? '';
  const dte = (v: string | null | undefined) => (v ? fmtLocalDate(v) : '');

  // Column sets mirror the legacy ListViews:
  //   Consumption -> Distribution_ListView_Consumption
  //   Disposal    -> Distribution_ListView_Disposal (Status is hidden there)
  /* ── Ticking a row STAGES the consumption (GoLive parity) ───────────────
     Owner 2026-09-30: "When click the checkbox then the ConsumptionDate and the
     ConsumptionBy was empty", with a GoLive screenshot showing both cells filled
     the moment three rows were ticked. AI left both blank and refused the Save
     until the user opened the combo on every row and picked a person.

     Legacy fills them on the grid itself. ConsumptionViewController.cs ~:251
     wires ClientSideEvents.SelectionChanged on Distribution_ListView_Consumption
     to a DevExpress batch edit, in three branches:
       visibleIndex -1, count 0  -> select-all UNticked: loop every visible row,
                                    clear ConsumptionDate + ConsumptionBy;
       visibleIndex -1, count >0 -> select-all ticked: loop every visible row,
                                    set ConsumptionDate = today and
                                    ConsumptionBy = cpuserid / cpusername;
       otherwise                 -> the one toggled row, same two outcomes.
     `cpuserid` is SecuritySystem.CurrentUserId and `cpusername` the current
     user's Employee.DisplayName, both pushed into JSProperties ~:230. So the
     tick pre-fills the CURRENT USER and Save commits it; the column stays
     editable, so "somebody else consumed it" is still one pick away, and
     Consume_Execute's "Select the consumedby." (~:518) only fires if you
     deliberately clear it.

     ⚠ ONE GUARD, and it is the ONLY one: `Status == 'PendingConsume'`. Every
     branch tests it before touching a cell. Legacy keeps it as a belt-and-braces
     check -- the list itself is already filtered on exactly that status
     (Criteria["filter"] = "[Status] = 'PendingConsume'",
     ConsumptionViewController :220/:569; AI_ICM_Consumption_List_SP mirrors it),
     so in practice no other status can reach a cell.

     Unlike Item Disposal, there is NO expiry condition anywhere on this form's
     tick OR on its list: an EXPIRED PendingConsume lot is listed and CAN be
     consumed (legacy Consume_Execute has no expiry check). Legacy spends the
     expiry rule elsewhere -- on the Consume POPUP / LT PICKER views (:351-361)
     and on Consume RETURN (:770, "ExpDateerror"). AI applied the picker's rule
     to this list by mistake until 2026-10-06 and so hid every expired lot:
     1,044 rows where G5 shows 2,021.

     HOW AI DOES IT. ConsumptionBy is a CHOSEN employee that is actually POSTed,
     so the tick seeds the REAL staged value (`consumedByRow`, the same map the
     inline combo writes) rather than painting a cell -- otherwise the grid would
     read "Administrator" while the save still refused. ConsumptionDate is
     display-only, like the disposal preview: AI_ICM_Consumption_Consume_SP
     stamps it server-side and the client value is never sent.
     The seed resolves the signed-in user against the SAME employee-options list
     the combo offers, so the staged name always matches a pickable option; if
     that user is not in the list nothing is seeded and the form behaves as it
     did before (pick a person by hand). */
  const previewBy = useAuthStore(st => st.user?.name) ?? '';
  const previewId = useAuthStore(st => st.user?.id) ?? '';
  const previewDate = useMemo(() => fmtLocalDate(new Date().toISOString()) ?? '', []);

  /** Employee.Oid == PermissionPolicyUser.Oid on this database (§14a Rule C),
   *  so the auth id normally IS the employee row; the name match is the
   *  fallback for a database where it is not. */
  const meOid = useMemo(() => {
    const byId = employees.find(e => (e.oid ?? '').toLowerCase() === previewId.toLowerCase());
    if (byId) return byId.oid;
    return employees.find(e => empName(e) === previewBy)?.oid ?? '';
  }, [employees, previewId, previewBy]);

  /** Legacy's `Status == 'PendingConsume'` test. StatusLabel is the SP's spaced
   *  label for enum 1. */
  const pendingConsume = (r: ConsumptionRow) => r.statusLabel === 'Pending Consume';

  /* The tick seeds / the untick clears ConsumptionBy, exactly as legacy's three
     SelectionChanged branches do. Keyed on the whole selection, so select-all,
     a single tick and a programmatic clear all run the same reconciliation. */
  useEffect(() => {
    if (formType !== 'consume' || viewMode || !meOid) return;
    setConsumedByRow(prev => {
      const next = { ...prev };
      let changed = false;
      for (const r of rows) {
        if (!pendingConsume(r)) continue;
        if (selected.has(r.oid)) {
          if (!next[r.oid]) { next[r.oid] = meOid; changed = true; }
        } else if (next[r.oid]) {
          delete next[r.oid]; changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [selected, rows, formType, viewMode, meOid]);

  /** ConsumptionDate is not stored until Save, so the ticked row shows today the
   *  way legacy's batch-edit buffer does. Display only. */
  const consumePreview = (r: ConsumptionRow) =>
    formType === 'consume' && !viewMode &&
    pendingConsume(r) && !r.consumptionDate &&
    selected.has(r.oid);

  const consumeCols: AppColumn<ConsumptionRow>[] = [
    { field: 'lt',                header: 'LT#',             width: 110, text: r => txt(r.lt),                render: r => txt(r.lt) },
    { field: 'vendorName',        header: 'Vendor',          width: 150, text: r => txt(r.vendorName),        render: r => txt(r.vendorName) },
    { field: 'itemName',          header: 'Item',            width: 190, text: r => txt(r.itemName),          render: r => txt(r.itemName) },
    // Legacy's live grid captions Item.Specification "Description" and shows
    // AmountUnits next to Amount; AI captioned it "Specification" and had no
    // unit column at all.
    { field: 'specification',     header: 'Description',     width: 200, text: r => txt(r.specification),     render: r => txt(r.specification) },
    /* Catalog# is on the HISTORY view (legacy
       Distribution_ListView_ConsumptionViewmode Index 2); the pending queue
       (Distribution_ListView_Consumption) does not show it. */
    ...(viewMode ? [
      { field: 'catalog', header: 'Catalog#', width: 130,
        text: (r: ConsumptionRow) => txt(r.catalog), render: (r: ConsumptionRow) => txt(r.catalog) },
    ] as AppColumn<ConsumptionRow>[] : []),
    { field: 'amount',            header: 'Amount',          width: 90,  text: r => txt(r.amount),            render: r => txt(r.amount) },
    { field: 'amountUnits',       header: 'AmountUnits',     width: 110, text: r => txt(r.amountUnits),       render: r => txt(r.amountUnits) },
    { field: 'storageName',       header: 'Storage',         width: 130, text: r => txt(r.storageName),       render: r => txt(r.storageName) },
    { field: 'expiryDate',        header: 'ExpDate',         width: 110, text: r => dte(r.expiryDate),        render: r => dte(r.expiryDate) },
    /* AlertQty is NOT on the legacy History view — it belongs to the pending
       queue, where a low stock level is actionable. */
    ...(viewMode ? [] : [
      { field: 'alertQty', header: 'AlertQty', width: 90, type: 'number' as const,
        text: (r: ConsumptionRow) => String(r.alertQty ?? ''), value: (r: ConsumptionRow) => r.alertQty ?? 0 },
    ] as AppColumn<ConsumptionRow>[]),
    { field: 'consumptionDate',   header: 'ConsumptionDate', width: 130,
      text:   r => consumePreview(r) ? previewDate : dte(r.consumptionDate),
      render: r => consumePreview(r) ? previewDate : dte(r.consumptionDate) },
    /* ConsumptionBy is EDITABLE on the pending queue and read-only on History.
       Legacy's Distribution_ListView_Consumption leaves the column editable (no
       AllowEdit="False") and Consume_Execute calls Grid.UpdateEdit() before
       reading it, i.e. you pick the person on the ROW, then Save. */
    { field: 'consumptionByName', header: 'ConsumptionBy', width: 150,
      text: r => txt(consumedName(r)), render: r => txt(consumedName(r)),
      /* The inline editor reads `value` (NOT `text`/`render`) for what the cell
         shows at rest, so the picked-but-unsaved name needs to come through
         here — same as DistributionListPage's Storage / GivenTo cells. */
      value: r => consumedName(r),
      ...(viewMode || formType === 'dispose' || !perm.write ? {} : {
        edit: {
          type: 'combo' as const,
          trigger: 'click' as const,
          options: employees.map(empName),
          requiredEmpty: (r: ConsumptionRow) => !consumedName(r),
        },
      }) },
    { field: 'statusLabel',       header: 'Status',          width: 130, type: 'status', text: r => r.statusLabel },
    /* ItemCode is NOT on the legacy History view
       (Distribution_ListView_ConsumptionViewmode lists Category . Item .
       Division . Specification . Catalog# . Vendor . LT# . Amount .
       AmontUnits . Storage . ExpDate . ConsumedDate . ConsumptionBy . Status --
       no ItemCode). It stays on the pending QUEUE, which was not reported. */
    ...(viewMode ? [] : [
      { field: 'itemCode', header: 'ItemCode', width: 120,
        text: (r: ConsumptionRow) => txt(r.itemCode), render: (r: ConsumptionRow) => txt(r.itemCode) },
    ] as AppColumn<ConsumptionRow>[]),
  ];

  /* ── Ticking a row PREVIEWS the disposal (GoLive parity) ──────────────────
     Owner 2026-09-30: "When click the checkbox then the DisposedDate and the
     DisposedBy was empty", with a GoLive screenshot showing both cells filled
     the moment three rows were ticked.

     Legacy does this client-side on the grid itself. DisposalViewController.cs
     ~:258 wires ClientSideEvents.SelectionChanged on
     Distribution_ListView_Disposal (non-View mode) to a DevExpress BATCH EDIT:
       selected   -> if BOTH cells are null, SetCellValue(DisposedDate, today)
                     and SetCellValue(DisposedBy, cpuserid, cpusername)
       deselected -> if that row HasChanges, set both back to null
     with cpusername = the current user's Employee.DisplayName, assigned
     server-side a few lines above. Select-all runs the same loop over every
     visible row. So the tick STAGES the disposal and Dispose commits it.

     AI's grid is not a DevExpress batch-edit grid, so the same effect is
     produced by deriving the two cells at render time from the selection. The
     value is never sent to the server -- Dispose already stamps both columns in
     AI_ICM_Disposal_Dispose_SP -- so this is display only, exactly like the
     legacy batch-edit buffer before its save.

     BOTH LEGACY GUARDS ARE KEPT:
       . fill only when the row has NO existing DisposedDate/DisposedBy, so an
         already-disposed row is never overwritten;
       . therefore unticking can only ever clear what the tick added.
     And it is scoped to the WORKING list (not History, not the consume form),
     which is the only place legacy attaches the script. */
  const disposalPreview = (r: ConsumptionRow) =>
    formType === 'dispose' && !viewMode &&
    !r.disposedDate && !r.disposedByName &&
    selected.has(r.oid);

  const disposeCols: AppColumn<ConsumptionRow>[] = [
    { field: 'lt',                header: 'LT',              width: 110, text: r => txt(r.lt),                render: r => txt(r.lt) },
    { field: 'itemName',          header: 'Item',            width: 190, text: r => txt(r.itemName),          render: r => txt(r.itemName) },
    { field: 'specification',     header: 'Description',     width: 200, text: r => txt(r.specification),     render: r => txt(r.specification) },
    { field: 'vendorName',        header: 'Vendor',          width: 150, text: r => txt(r.vendorName),        render: r => txt(r.vendorName) },
    { field: 'expiryDate',        header: 'ExpDate',         width: 110, text: r => dte(r.expiryDate),        render: r => dte(r.expiryDate) },
    { field: 'receiveDate',       header: 'ReceivedDate',    width: 120, text: r => dte(r.receiveDate),       render: r => dte(r.receiveDate) },
    { field: 'consumptionDate',   header: 'ConsumptionDate', width: 130, text: r => dte(r.consumptionDate),   render: r => dte(r.consumptionDate) },
    { field: 'consumptionByName', header: 'ConsumptionBy',   width: 150, text: r => txt(r.consumptionByName), render: r => txt(r.consumptionByName) },
    { field: 'disposedDate',      header: 'DisposedDate',    width: 120,
      text:   r => disposalPreview(r) ? previewDate : dte(r.disposedDate),
      render: r => disposalPreview(r) ? previewDate : dte(r.disposedDate) },
    { field: 'disposedByName',    header: 'DisposedBy',      width: 150,
      text:   r => disposalPreview(r) ? previewBy : txt(r.disposedByName),
      render: r => disposalPreview(r) ? previewBy : txt(r.disposedByName) },
  ];

  const cols = formType === 'dispose' ? disposeCols : consumeCols;

  /** Consume Return — legacy ConsumeReturn_Execute (ConsumptionViewController
   *  ~757): un-consume an item that was consumed by mistake, putting it back on
   *  the shelf. Legacy exposes it ONLY on the view-mode list
   *  (ConsumeReturn.TargetViewId = "Distribution_ListView_ConsumptionViewmode"),
   *  which is this page's History mode — a row still pending consumption has
   *  nothing to return.
   *
   *  Legacy asks for the reason in an ICMRollBack popup before doing anything,
   *  and refuses an item that is still Pending Consume or already expired. The
   *  SP re-checks both, so these are the friendly half of the guards. */
  async function handleConsumeReturn() {
    const ids = shownTicks;
    if (ids.length === 0) { notify.warning('Select at least one consumed item to return.'); return; }

    if (!(await confirm({
      title: 'Consume Return?',
      message: `Return ${ids.length} consumed item(s) to stock?`,
      tone: 'danger',
      confirmLabel: 'Consume Return',
    }))) return;

    const reason = await prompt({
      title: 'Consume Return',
      message: 'Reason for the return',
      placeholder: 'Why is this consumption being reversed?',
      required: true,
    });
    if (reason == null) return;

    setReturning(true);
    try {
      for (const id of ids) {
        await api.post(`/api/v1/consumptions/${id}/consume-return`, { reason });
      }
      setSelected(new Set());
      await load();
      notify.success('Consume Return completed successfully.');
    } catch (err) {
      // The SP's guards ("still pending consumption", "an expired item cannot be
      // returned", "a reason is required") come back as a 400 BUSINESS_RULE, so
      // show what it actually said.
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      notify.error(msg ?? 'Consume Return failed.');
    } finally {
      setReturning(false);
    }
  }

  /** DISPOSE RETURN — legacy's DisposeReturn on the disposal History view
   *  (DisposalViewController ~628): un-dispose the selected rows, recording why.
   *  Legacy asks for the reason first, as the other ICM returns do. */
  async function handleDisposeReturn() {
    const ids = shownTicks;
    if (ids.length === 0) { notify.warning('Select at least one disposed item to return.'); return; }

    if (!(await confirm({
      title: 'Dispose Return?',
      message: `Return ${ids.length} disposed item(s)?`,
      tone: 'danger',
      confirmLabel: 'Dispose Return',
    }))) return;

    const reason = await prompt({
      title: 'Dispose Return',
      message: 'Reason for the return',
      placeholder: 'Why is this disposal being reversed?',
      required: true,
    });
    if (reason == null) return;

    setReturning(true);
    try {
      for (const id of ids) {
        await api.post(`/api/v1/disposals/${id}/dispose-return`, { reason });
      }
      setSelected(new Set());
      await load();
      notify.success('Dispose Return completed successfully.');
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      notify.error(msg ?? 'Dispose Return failed.');
    } finally {
      setReturning(false);
    }
  }

  /** Legacy's barcode action filters the grid to the scanned LT ([LT] = ?) and
   *  says so when the LT is not in the list, instead of leaving an empty grid
   *  with no explanation (ConsumptionViewController.TextBoxAction_ValueChanged). */
  function applyBarcode() {
    const lt = barcode.trim();
    setLtFilter(lt);
    if (lt && !rows.some(r => (r.lt ?? '').toLowerCase() === lt.toLowerCase())) {
      notify.warning('Invalid Data');
    }
  }

  /** What the ConsumptionBy cell shows: the row's stored name, or the one just
   *  picked in the grid and not yet saved. */
  const consumedName = (r: ConsumptionRow) => {
    const pending = consumedByRow[r.oid];
    if (pending) {
      const o = employees.find(e => e.oid === pending);
      if (o) return empName(o);
    }
    return r.consumptionByName ?? '';
  };

  async function handleAction() {
    if (selected.size === 0) { notify.warning('Select at least one row.'); return; }
    const ids = Array.from(selected) as string[];
    // Legacy refuses the save until every ticked row has a Consumed By
    // (ConsumptionViewController ~518, "Select the consumedby."). The server
    // enforces it too; this is the UX half.
    if (formType === 'consume' && ids.some(id => !consumedByRow[id])) {
      notify.warning('Select the consumedby.'); return;
    }
    if (!(await confirm({
      title: `${actionLabel}?`,
      message: `${actionLabel} ${ids.length} item(s)?`,
      tone: formType === 'dispose' ? 'danger' : undefined,
      confirmLabel: actionLabel,
    }))) return;
    setActing(true);
    try {
      await Promise.all(ids.map(id =>
        api.post(`${baseUrl}/${id}/${actionVerb}`,
          formType === 'consume' ? { consumedBy: consumedByRow[id] } : {})));
      setSelected(new Set());
      setConsumedByRow({});
      await load();
      notify.success(`${actionLabel}d successfully.`);
    } catch {
      notify.error(`${actionLabel} failed.`);
    } finally {
      setActing(false);
    }
  }

  /* Search covers every visible column PLUS what G5's full-text search also reads:
     VendorLT, Catalog#, DisposedBy and the server's hiddenSearchText. Proven against
     three G5 results on the queue (bug report 2026-11-02): "%" finds 94, "z" finds 1,237
     (121 only in VendorLT) and "f" finds all 1,961 (1,250 only in itemreceived, "4 of 4").
     Here rather than beside `cols` because a column's text can call helpers declared
     above this point. Memoised on the data, not on `cols`, which is rebuilt every render:
     the grid treats a new `rows` array as a reload and refocuses, and ticking a row must
     not re-run the search. */
  const colsRef = useRef(cols);
  colsRef.current = cols;
  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r =>
      colsRef.current.some(c => (c.text?.(r) ?? '').toLowerCase().includes(t))
      || [r.vendorLt, r.catalog, r.givenToName, r.disposedByName, r.hiddenSearchText]
           .some(v => (v ?? '').toLowerCase().includes(t)));
  }, [rows, search, viewMode, formType]);

  /* The ticks the user can see. The Return buttons enable on these and act on these,
     never on a tick a Date Filter or search has hidden (owner request 2026-11-02:
     "without check the checkbox, disable the consume return button"). */
  const shownTicks = filtered.filter(r => selected.has(r.oid)).map(r => r.oid);

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>ICM Operations</span><i className="ti ti-chevron-right" />
            {viewMode ? (
              <>
                {/* The way BACK, now that History drops the "Pending" button. */}
                <span onClick={() => { setSelected(new Set()); setBarcode(''); setLtFilter(''); setViewMode(false); }}
                      style={{ cursor: 'pointer' }}>{title}</span>
                <i className="ti ti-chevron-right" />
                <span className="dp-bc-active">
                  {formType === 'dispose' ? 'Item Disposal History' : 'Item Consumption History'}
                </span>
              </>
            ) : (
              <span className="dp-bc-active">{title}</span>
            )}
          </div>
          <div className="dp-title-row">
            <FormTitle
              pageId={pageId} auditFormName={formType === 'dispose' ? 'Item Disposal' : 'Item Consumption'}
              title={!viewMode ? title
                : formType === 'dispose' ? 'Item Disposal History' : 'Item Consumption History'}
              loadMs={loadMs}
              loading={loading}
            />
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={
              <>
                {/* Legacy's consumption bar calls the commit button SAVE (you
                    tick the rows and save). Disposal keeps its own caption. */}
                {/* SAVE is the queue's commit action (legacy Consume, targeted at
                    Distribution_ListView_Consumption ONLY). It has nothing to do
                    on the History view, where everything is already consumed. */}
                {!viewMode && perm.write && (
                  <button className="ca-btn" disabled={selected.size === 0 || acting} onClick={handleAction}>
                    <i className={`ti ti-${formType === 'dispose' ? 'trash' : 'device-floppy'}`} /> {actionLabel}
                  </button>
                )}
                {/* HISTORY — legacy's Consumeview: the VIEW-MODE toggle. It
                    opens Distribution_ListView_ConsumptionViewmode, whose
                    Criteria is EMPTY, i.e. every record instead of only the
                    pending ones. (An earlier pass wired this caption to the
                    per-row popup below, which is a DIFFERENT feature -- that one
                    is now "Item History".) */}
                {/* The History toggle belongs to the QUEUE only — legacy's
                    Consumeview targets Distribution_ListView_Consumption, and the
                    view it opens has no way back on its bar. The breadcrumb is
                    the way back. */}
                {!viewMode && (
                  <button className="ca-btn" style={{ marginLeft: 6 }}
                          onClick={() => { setSelected(new Set()); setBarcode(''); setLtFilter(''); setViewMode(true); }}
                          title={formType === 'dispose'
                            ? 'Show the items already disposed'
                            : 'Show every consumption record'}>
                    <i className="ti ti-history" /> History
                  </button>
                )}
                {/* CONSUME RETURN — legacy puts it on the view-mode list ONLY
                    (ConsumeReturn.TargetViewId =
                    Distribution_ListView_ConsumptionViewmode), so it appears
                    with History and not on the pending list, where there is
                    nothing consumed to return. */}
                {formType !== 'dispose' && viewMode && perm.write && (
                  <button className="ca-btn" style={{ marginLeft: 6 }}
                          disabled={shownTicks.length === 0 || returning}
                          onClick={handleConsumeReturn}
                          title="Return the selected consumed item(s) to stock">
                    <BusyIcon busy={returning} icon="ti-arrow-back-up" /> Consume Return
                  </button>
                )}
                {/* DISPOSE RETURN — legacy's DisposeReturn, which
                    DisposalView_Execute switches ON with the History view (and
                    which is off on the working list, where nothing is disposed
                    yet). Un-disposes the selected rows. */}
                {formType === 'dispose' && viewMode && perm.write && (
                  <button className="ca-btn" style={{ marginLeft: 6 }}
                          disabled={shownTicks.length === 0 || returning}
                          onClick={handleDisposeReturn}
                          title="Return the selected disposed item(s)">
                    <BusyIcon busy={returning} icon="ti-arrow-back-up" /> Dispose Return
                  </button>
                )}
                {/* The per-row "Item History" popup button was REMOVED on
                    2026-09-26 (owner decision). Legacy has no such button; it had
                    been added because dbo.ConsumptionHistory was otherwise
                    unreachable. The API (GET /consumptions/{id}/history) and its
                    SP are LEFT IN PLACE for a future Item History screen. */}
                {/* The Date Filter is legacy's ConsumeDateFilter, targeted at the
                    view-mode list — the pending list is short and has nothing to
                    window. */}
                {/* Legacy hides the Date Filter on the disposal WORKING list
                    (OnActivated turns DisposalDateFilter off) and switches it on
                    with History; Consumption does the same. */}
                {viewMode && (
                  <DateFilterSelect value={dateFilter} onChange={setDateFilter} />
                )}

              </>
            }
            /* Search moves INTO the toolbar, where legacy has it, instead of
               sitting on a row of its own below the bar. */
            right={
              <div className="list-search" style={{ minWidth: 240 }}>
                <span className="tb-i">🔍</span>
                <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Text to search…" />
                {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
              </div>
            }
            onRefresh={load}
            refreshing={loading}
            /* MODE sits AFTER Refresh and BEFORE Export, which is where GoLive
               puts it (Dispose · History · Refresh · Mode · Export to · Search).
               The shared toolbar already has a slot for exactly this position,
               so no per-form bar is hand-rolled (standing rule 9). Legacy
               captions the action "Mode" and turns it off in History
               (DisposalView_Execute) — nothing left to filter by expiry there. */
            /* GoLive order on this bar: Save . History . Refresh .
               [barcode box] Barcode . Export to -- so the scan controls sit
               between Refresh and Export, and Item Disposal's Mode dropdown
               shares the slot. */
            beforeExport={<>
                {/* BARCODE — scan or type an LT and the grid shows THAT item,
                    exactly as legacy's barcode box does ([LT] = ?). Enter
                    applies it; clearing the box restores the full list.

                    ONE bordered box with the caption welded inside its right
                    edge, via the shared `.ai-lkbox` primitive (toolbar variant).
                    GoLive renders this as a DevExpress ParametrizedAction — a
                    SINGLE control, not a detached field plus a separate button —
                    so the two were fused rather than a third bar style invented
                    (standing rules 8 + 9). The clear ✕ joins the same box and
                    only appears once a filter is actually on.

                    HISTORY DOES NOT GET IT (owner request 2026-09-30, screenshot
                    of the box on Item Consumption History). Legacy scopes the
                    action to the pending queue and nothing else --
                    ConsumptionViewController.cs :43
                    `txtBarcodeActionConsumption.TargetViewId =
                     "Distribution_ListView_Consumption"` -- so the view-mode list
                    Distribution_ListView_ConsumptionViewmode never renders it.
                    Scanning a lot is how you find the row you are about to
                    consume; History is read-only, and its Date Filter + search
                    are what narrow it. */}
                {formType !== 'dispose' && !viewMode && (
                  <div className="ai-lkbox ai-lkbox-tb" style={{ width: 250, marginLeft: 8 }}>
                    <input
                      className="sd-input ai-lk-ctl"
                      value={barcode}
                      placeholder="LT#"
                      title="Scan or type an LT number, then press Enter"
                      onChange={e => setBarcode(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); applyBarcode(); }
                        if (e.key === 'Escape') { setBarcode(''); setLtFilter(''); }
                      }}
                    />
                    {ltFilter && (
                      <button type="button" className="ai-lk-btn clr"
                              onClick={() => { setBarcode(''); setLtFilter(''); }}
                              title="Show all items again">
                        <i className="ti ti-x" />
                      </button>
                    )}
                    <button type="button" className="ai-lk-btn ai-lk-cap"
                            onClick={applyBarcode}
                            title="Show the item with this LT number">
                      Barcode
                    </button>
                  </div>
                )}
              {formType === 'dispose' && !viewMode && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 12 }}>Mode</span>
                <select
                  className="sd-input"
                  style={{ width: 170 }}
                  value={disposalMode}
                  onChange={e => { setSelected(new Set()); setDisposalMode(e.target.value as DisposalMode); }}
                  title="Expired / non-expired / all (legacy Mode)"
                >
                  {DISPOSAL_MODES.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </span>
              )}
            </>}
            exportData={{ filename: formType === 'dispose' ? 'disposals' : 'consumptions', ...gridExportMatrix(filtered, cols) }}
          />
        </div>
        <div className="bl-card bl-card-flex">
          {error ? (
            <div className="bl-err"><i className="ti ti-alert-circle" /> {error}</div>
          ) : (
            <AppDataGrid<ConsumptionRow>
              rows={filtered}
              columns={cols}
              getRowId={r => r.oid}
              loading={loading}
              loadMs={loadMs}
              selectable
              selectedIds={selected}
              onSelectionChange={setSelected}
              onCellCommit={(row, column, newValue) => {
                if (column.field !== 'consumptionByName') return;
                const picked = employees.find(o => empName(o) === newValue);
                setConsumedByRow(m => {
                  const next = { ...m };
                  if (picked) next[row.oid] = picked.oid; else delete next[row.oid];
                  return next;
                });
              }}
              emptyText={`No ${formType} items found.`}
            />
          )}
        </div>
      </div>

    </div>
  );
}
